import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "@/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { IconBan, IconCircleCheck } from "@tabler/icons-react";
import { stepChanged, householdDataSaved } from "@/app/booking-flow/publicBookingFlowSlice";
import type { Household } from "@/app/booking-flow/publicBookingFlowTypes";
import { useGetPublicBookingSeasonQuery } from "@/app/appointments/booking-season/bookingSeasonApi";
import {
  everyProgramSwitchedOff,
  householdProgramLines,
  type HouseholdProgramLine,
} from "@/app/booking-flow/fundingSwitchNotices";

// Where each line's reason is found by tests and assistive technology, by why it is not offered.
function reasonTestId(line: HouseholdProgramLine): string {
  if (line.status === "alreadyReceived") {
    return line.program === "Liheap" ? "liheap-already-received" : "pse-help-already-assigned";
  }
  return line.program === "Liheap" ? "funding-switch-liheap" : line.program === "PseHelpGas" ? "funding-switch-gas" : "funding-switch-electric";
}

/**
 * The programs the chosen energy answers lead to (AB#607/AB#595 amendment), one line each. The energy
 * source and service are facts about the household and stay selectable; a program that is switched off
 * or already received is greyed here instead, marked with an icon and a word so the state does not rest
 * on colour alone, and its reason inline. Consecutive lines with the same reason (PSE HELP gas and
 * electric, both received) say it once, beneath the last of them.
 */
function ProgramLines({ lines }: { lines: HouseholdProgramLine[] }) {
  return (
    <div className="space-y-1.5">
      <Label id="household-programs-label">Programs</Label>
      <ul aria-labelledby="household-programs-label" className="space-y-1.5">
        {lines.map((line, index) => {
          const id = `household-program-${line.program}`;
          if (!line.reason) {
            return (
              <li key={line.program} id={id} data-testid={id} className="text-sm text-slate-700">
                {line.label}
              </li>
            );
          }
          // The reason is said by the last line of a run that shares it; every line in the run points at it.
          let owner = index;
          while (owner + 1 < lines.length && lines[owner + 1]!.reason === line.reason) owner++;
          const reasonId = `household-program-${lines[owner]!.program}-reason`;
          const Icon = line.status === "alreadyReceived" ? IconCircleCheck : IconBan;
          return (
            <li key={line.program} id={id} data-testid={id} aria-disabled="true" aria-describedby={reasonId} className="space-y-1 text-sm text-slate-500">
              <span className="flex items-center gap-2">
                {line.label}
                <span className="inline-flex items-center gap-1 text-xs" data-testid={`${id}-unavailable`}>
                  <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                  {line.status === "alreadyReceived" ? "Already received" : "Unavailable"}
                </span>
              </span>
              {owner === index && (
                <p role="note" id={reasonId} data-testid={reasonTestId(line)} className="pl-4 text-xs text-slate-600">
                  {line.reason}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function GatherHouseholdDataStep() {
  const dispatch = useAppDispatch();

  const receivedPrograms = useAppSelector(state => state.publicBookingFlow.receivedPrograms);
  const savedHouseholdData = useAppSelector(state => state.publicBookingFlow.householdData);
  // The page already holds the season; this reads the same cached answer.
  const season = useGetPublicBookingSeasonQuery().data;
  // AB#840: while AmsBookingWithoutIncome is on, the step does not ask for income at all.
  const withoutIncome = season?.amsBookingWithoutIncome === true;

  const [formData, setFormData] = useState<Household>(
    savedHouseholdData ?? {
      householdSize: -1,
      householdIncome: -1,
      address: "",
      energySource: "Other Heating and Energy Source",
      pseService: undefined,
    }
  );

  // The programs the chosen answers lead to, as the server derives them, each with why it is not
  // offered. The step refuses only what the server's switch refusal would: every one switched off.
  const programLines = householdProgramLines(formData.energySource, formData.pseService, season?.programs, receivedPrograms);
  const noProgramRemains = everyProgramSwitchedOff(programLines);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Answers whose every program is switched off are never submitted, however the form was sent.
    if (!isFormValid) return;
    if (withoutIncome) {
      // Not asked, so not sent: an answer saved before the flag changed is dropped with the field.
      const { householdIncome: _notAsked, ...withoutIncomeData } = formData;
      dispatch(householdDataSaved(withoutIncomeData));
    } else {
      dispatch(householdDataSaved(formData));
    }
    dispatch(stepChanged(4));
  };

  const handleBack = () => {
    dispatch(stepChanged(2));
  };

  const isFormValid =
    formData.address.trim().length >= 5 &&
    formData.householdSize >= 1 &&
    formData.householdSize <= 24 &&
    (withoutIncome || (formData.householdIncome ?? -1) >= 0) &&
    (formData.energySource !== "PSE" || !!formData.pseService) &&
    !noProgramRemains;

  return (
    <Card className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
      <CardHeader className="text-center px-4">
        <CardTitle className="text-2xl font-semibold text-slate-900">Household & Energy Details</CardTitle>
        <CardDescription className="text-slate-500">
          This information helps us determine the right assistance program for you.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form id="step2-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="address">Home Address</Label>
            <Input required minLength={5} id="address" name="address" placeholder="123 Example St, Sample City, WA" value={formData.address} onChange={handleChange} />
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="householdSize">Household Size (1-24)</Label>
              <p id="household-size-note" className="text-sm text-slate-500">
                Enter the number of people living in your household including yourself.
              </p>
              <Input required id="householdSize" name="householdSize" type="number" min={1} max={24} value={formData.householdSize > -1 ? formData.householdSize : ""} onChange={handleChange} onFocus={(e) => e.target.select()} aria-describedby="household-size-note" />
            </div>
            {!withoutIncome && (
              <div className="space-y-1.5">
                <Label htmlFor="householdIncome">Monthly Income ($)</Label>
                <p id="household-income-note" className="text-sm text-slate-500">
                  Enter the total monthly income from everyone in your household combined.
                </p>
                <Input required id="householdIncome" name="householdIncome" type="number" min={0} value={(formData.householdIncome ?? -1) > -1 ? formData.householdIncome : ""} onChange={handleChange} onFocus={(e) => e.target.select()} aria-describedby="household-income-note" />
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Heating and Energy Source</Label>
            <RadioGroup
              value={formData.energySource}
              onValueChange={(v) =>
                setFormData((p) => ({ ...p, energySource: v as any, pseService: v === "PSE" ? p.pseService : undefined }))
              }
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="PSE" id="energy-source-pse" />
                <Label htmlFor="energy-source-pse" className="font-normal">
                  Puget Sound Energy
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="Other Heating and Energy Source" id="energy-source-other" />
                <Label htmlFor="energy-source-other" className="font-normal">
                  Other Heating and Energy Source
                </Label>
              </div>
            </RadioGroup>
          </div>

          {formData.energySource === "PSE" && (
            <div className="space-y-1.5">
              <Label>PSE Service</Label>
              <RadioGroup
                value={formData.pseService ?? ""}
                onValueChange={(v) => setFormData((p) => ({ ...p, pseService: v as any }))}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="Gas" id="pse-source-gas" />
                  <Label htmlFor="pse-source-gas" className="font-normal">Gas</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="Electric" id="pse-source-electric" />
                  <Label htmlFor="pse-source-electric" className="font-normal">Electric</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="Both gas and electric" id="pse-source-both" />
                  <Label htmlFor="pse-source-both" className="font-normal">Both gas and electric</Label>
                </div>
              </RadioGroup>
            </div>
          )}

          <ProgramLines lines={programLines} />
        </form>

        <div className="flex flex-col sm:flex-row gap-3 mt-6 w-full max-w-md mx-auto">
          <Button type="button" variant="outline" className="flex flex-grow-1 text-base h-12" onClick={handleBack}>
            Back
          </Button>
          <Button
            form="step2-form"
            type="submit"
            className="flex flex-grow-2 text-base h-12"
            disabled={!isFormValid}
          >
            Continue
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
